/**
 * The name of an ERP record, as **text**. Never a link.
 *
 * OneSphere's users work OneSphere's modules. Anything they need from the ERP is exposed in a OneSphere screen —
 * finance's document registers on `/finance`, the ERP card on a record, the fleet's ERP hub — and nothing in this
 * application links out to ERPNext. An administrator who needs the desk opens **ERPNext's own URL** directly; they
 * do not arrive there through a link in here (owner's rule, 2026-09-28, superseding the super-administrator link
 * of 2026-09-21).
 *
 * The component is kept rather than deleted from its twenty-nine call sites because the rule then lives in one
 * place, which is why it existed in the first place: `to` and `href` are accepted and deliberately ignored, so no
 * caller can reintroduce a link by passing one. `useIsSuperAdmin()` remains for the blocks that hide entirely.
 */
import type { ReactNode } from "react";
import { useAuth } from "../auth/AuthProvider";
import { isSuperAdmin } from "../lib/auth";

export function useIsSuperAdmin(): boolean {
  const { identity } = useAuth();
  return isSuperAdmin(identity?.roles ?? []);
}

export function ErpLink({
  children,
  fallback,
}: {
  /** Accepted and ignored: an old OneSphere `/erp/...` address. */
  to?: string;
  /** Accepted and ignored: an address on the ERP site itself. */
  href?: string | null;
  className?: string;
  children: ReactNode;
  /** What to show instead of the record's name, if a caller wants something other than the plain text. */
  fallback?: ReactNode;
  [key: string]: unknown;
}) {
  if (fallback !== undefined) return <>{fallback}</>;
  return <span className="text-inherit">{children}</span>;
}
